import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ResponsiveContainer, LineChart, Line, Tooltip } from "recharts";
import { formatCurrency } from "../../lib/utils";
import { totalRooms } from "../../lib/rooms";
import { Deal } from "../../types/deal";

interface DealSnapshotProps {
  deal: Deal;
  revparByYear: number[];
  ebitdaByYear: number[];
  leveredCFByYear: number[];
  exitYear: number;
  netSaleProceeds: number;
}

const CustomTooltip = ({ active, payload, label, currency, suffix }: any) => {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
      <p className="font-semibold text-slate-900">Year {label}</p>
      <p className="text-sm text-slate-700">
        {suffix === "k" ? `€${value}k` : formatCurrency(value, currency)}
      </p>
    </div>
  );
};

export default function DealSnapshot({
  deal,
  revparByYear,
  ebitdaByYear,
  leveredCFByYear,
  exitYear,
  netSaleProceeds,
}: DealSnapshotProps) {
  console.log("rooms", deal);
  const projectCost =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.budget?.grandTotal ||
    0;
  const financing = deal.kpis?.financing;
  const exit = deal.kpis?.exit;

  const revparData = revparByYear.slice(0, exitYear).map((value, i) => ({
    year: i + 1,
    value: Math.round(value),
  }));

  const ebitdaData = ebitdaByYear.slice(0, exitYear).map((value, i) => ({
    year: i + 1,
    value: Math.round(value / 1000),
  }));

  const cashflowData = leveredCFByYear
    .slice(0, exitYear + 1)
    .map((value, i) => ({
      year: i,
      value: Math.round(value / 1000),
    }));

  const sparklineProps = {
    type: "monotone" as const,
    strokeWidth: 2,
    dot: false,
  };

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="text-lg">Deal Snapshot</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Rooms:</span>
              <span className="font-medium text-slate-900">
                {deal.kpis.roomMix.total}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Total Investment:</span>
              <span className="font-medium text-slate-900">
                {formatCurrency(projectCost, deal.currency)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Net Sale Proceeds:</span>
              <span className="font-medium text-slate-900">
                {formatCurrency(netSaleProceeds, deal.currency)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Exit Year:</span>
              <span className="font-medium text-slate-900">
                Year {exitYear}
              </span>
            </div>
            {exit?.strategy === "SALE" && exit.sale?.exitCapRate && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Exit Cap Rate:</span>
                <span className="font-medium text-slate-900">
                  {exit.sale.exitCapRate}%
                </span>
              </div>
            )}
            {financing && financing.ltcPct > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">LTV:</span>
                  <span className="font-medium text-slate-900">
                    {financing.ltcPct}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Interest Rate:</span>
                  <span className="font-medium text-slate-900">
                    {financing.interestRatePct}%
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">
                RevPAR Trend
              </h4>
              <div className="h-16">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revparData}>
                    <Line
                      {...sparklineProps}
                      dataKey="value"
                      stroke="#14b8a6"
                    />
                    <Tooltip
                      content={(props) => (
                        <CustomTooltip
                          {...props}
                          currency={deal.currency}
                          suffix="full"
                        />
                      )}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">
                EBITDA Trend (k)
              </h4>
              <div className="h-16">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={ebitdaData}>
                    <Line
                      {...sparklineProps}
                      dataKey="value"
                      stroke="#3b82f6"
                    />
                    <Tooltip
                      content={(props) => (
                        <CustomTooltip
                          {...props}
                          currency={deal.currency}
                          suffix="k"
                        />
                      )}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">
                Levered Cash Flow (k)
              </h4>
              <div className="h-16">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cashflowData}>
                    <Line
                      {...sparklineProps}
                      dataKey="value"
                      stroke="#10b981"
                    />
                    <Tooltip
                      content={(props) => (
                        <CustomTooltip
                          {...props}
                          currency={deal.currency}
                          suffix="k"
                        />
                      )}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
